// Formatage FR commun à tout le back-office (aucune dépendance React).
//  - dates  : « 31 oct. 2026 14:30 » (heure de Paris, métier en France)
//  - montants : « 32,00 € »

export const PARIS_TZ = "Europe/Paris";

// Accepte epoch ms (nombre ou chaîne numérique), ISO 8601, Date.
export const toDate = (v) => {
  // 0 / valeurs négatives = « non renseigné » côté backend (long par défaut).
  if (v == null || v === "" || v === 0 || v === "0") return null;
  if (typeof v === "number" && v < 0) return null;
  if (v instanceof Date) return isNaN(v.getTime()) ? null : v;
  let d;
  if (typeof v === "number") d = new Date(v);
  else if (/^\d{9,}$/.test(String(v))) d = new Date(Number(v));
  else d = new Date(Date.parse(String(v)));
  return isNaN(d.getTime()) ? null : d;
};

const partsOf = (d, opts) => {
  const out = {};
  new Intl.DateTimeFormat("fr-FR", { timeZone: PARIS_TZ, ...opts })
    .formatToParts(d)
    .forEach(({ type, value }) => {
      out[type] = value;
    });
  return out;
};

// « 31 oct. 2026 »
export const fmtDate = (v, empty = "—") => {
  const d = toDate(v);
  if (!d) return empty;
  const p = partsOf(d, { day: "numeric", month: "short", year: "numeric" });
  return `${p.day} ${p.month} ${p.year}`;
};

// « 31 oct. 2026 14:30 »
export const fmtDateTime = (v, empty = "—") => {
  const d = toDate(v);
  if (!d) return empty;
  const p = partsOf(d, {
    day: "numeric",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    hourCycle: "h23",
  });
  return `${p.day} ${p.month} ${p.year} ${p.hour}:${p.minute}`;
};

// « 14:30 »
export const fmtTime = (v, empty = "—") => {
  const d = toDate(v);
  if (!d) return empty;
  const p = partsOf(d, { hour: "2-digit", minute: "2-digit", hourCycle: "h23" });
  return `${p.hour}:${p.minute}`;
};

// « il y a 3 j » — relatif court.
export const fmtRelative = (v, now = Date.now()) => {
  const d = toDate(v);
  if (!d) return "—";
  const diff = Math.round((now - d.getTime()) / 1000);
  const abs = Math.abs(diff);
  const fut = diff < 0;
  const unit = abs < 60 ? [abs, "s"] : abs < 3600 ? [Math.round(abs / 60), "min"]
    : abs < 86400 ? [Math.round(abs / 3600), "h"] : [Math.round(abs / 86400), "j"];
  return fut ? `dans ${unit[0]} ${unit[1]}` : `il y a ${unit[0]} ${unit[1]}`;
};

const EUR_FMT = new Intl.NumberFormat("fr-FR", { style: "currency", currency: "EUR" });
// Espaces fines insécables (U+202F) → espaces insécables classiques : rendu
// homogène quelle que soit la police (DM Sans n'a pas toujours le glyphe).
const nbsp = (s) => s.replace(/\u202f/g, "\u00a0");
const EUR = { format: (n) => nbsp(EUR_FMT.format(n)) };

// Montant en euros → « 32,00 € »
export const fmtMoney = (euros, empty = "—") => {
  if (euros == null || euros === "") return empty;
  const n = Number(euros);
  if (isNaN(n)) return empty;
  return EUR.format(n);
};

// Montant en centimes (Stripe) → « 32,00 € »
export const fmtCents = (cents, empty = "—") => {
  if (cents == null || cents === "") return empty;
  const n = Number(cents);
  if (isNaN(n)) return empty;
  return EUR.format(n / 100);
};

export const fmtNumber = (n) => nbsp(new Intl.NumberFormat("fr-FR").format(Number(n) || 0));

export const fullName = (first, last, fallback = "") =>
  `${first || ""} ${last || ""}`.replace(/\s+/g, " ").trim() || fallback;

export const initialsOf = (first, last) =>
  `${(first || "").trim()[0] || ""}${(last || "").trim()[0] || ""}`.toUpperCase() || "?";

// Un champ est-il un identifiant technique (masqué dans les listes, déplacé
// dans « Informations techniques » des fiches) ?
//   id, uid, uuid, idrainbow, *Id (patientId, proId…), id* (idSpeciality),
//   *Intent / stripe*Id, sessionId, roomId…
export const isTechnicalKey = (key) => {
  if (typeof key !== "string" || !key) return false;
  const last = key.split(".").pop();
  return (
    /^(id|uid|uuid|_id|idrainbow|suggestedBy|user)$/i.test(last) ||
    /[a-z]Id$/.test(last) ||
    /^id[A-Z]/.test(last) ||
    /Ids$/.test(last) ||
    /^(paymentIntent|customerId|transferId|payoutId|stripeAccountId|idempotencyKey|cardId)$/.test(last)
  );
};
